import React from "react";
import { motion, Variants } from "framer-motion";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@heroui/modal";

export interface EntityDetailField {
  label: string;
  value: React.ReactNode;
  fullWidth?: boolean;
}

export interface EntityDetailSection {
  title: string;
  icon?: string;
  fields: EntityDetailField[];
}

export type EntityDetailsBadgeTone =
  | "success"
  | "danger"
  | "warning"
  | "info"
  | "default";

export interface EntityDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  eyebrow?: string;
  avatarUrl?: string | null;
  avatarIcon?: string;
  badge?: { label: string; tone: EntityDetailsBadgeTone };
  sections: EntityDetailSection[];
  onEdit?: () => void;
}

export const EntityDetailLink: React.FC<{
  href: string;
  label: React.ReactNode;
}> = ({ href, label }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    style={{
      color: "#4f46e5",
      textDecoration: "none",
      fontWeight: 500,
      transition: "color 0.15s",
    }}
    onMouseEnter={(e) => { e.currentTarget.style.color = "#4338ca"; e.currentTarget.style.textDecoration = "underline"; }}
    onMouseLeave={(e) => { e.currentTarget.style.color = "#4f46e5"; e.currentTarget.style.textDecoration = "none"; }}
  >
    {label}
  </a>
);

const toneBadgeStyles: Record<EntityDetailsBadgeTone, { bg: string; color: string; border: string }> = {
  success: { bg: "#ecfdf5", color: "#059669", border: "#a7f3d0" },
  danger: { bg: "#fff1f2", color: "#e11d48", border: "#fecdd3" },
  warning: { bg: "#fffbeb", color: "#d97706", border: "#fde68a" },
  info: { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" },
  default: { bg: "#f8fafc", color: "#475569", border: "#e2e8f0" },
};

const toneClasses: Record<EntityDetailsBadgeTone, string> = {
  success: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  danger: "bg-rose-50 text-rose-700 ring-rose-600/20",
  warning: "bg-amber-50 text-amber-700 ring-amber-600/20",
  info: "bg-sky-50 text-sky-700 ring-sky-600/20",
  default: "bg-slate-100 text-slate-600 ring-slate-500/20",
};

const toneIconBg: Record<EntityDetailsBadgeTone, string> = {
  success: "linear-gradient(135deg, #10b981, #059669)",
  danger: "linear-gradient(135deg, #f43f5e, #e11d48)",
  warning: "linear-gradient(135deg, #f59e0b, #d97706)",
  info: "linear-gradient(135deg, #3b82f6, #2563eb)",
  default: "linear-gradient(135deg, #6366f1, #4f46e5)",
};

const priorityToneMap: Record<string, EntityDetailsBadgeTone> = {
  high: "danger",
  urgent: "danger",
  medium: "warning",
  normal: "warning",
  low: "info",
};

export const PriorityBadge: React.FC<{ priority?: string | null }> = ({
  priority,
}) => {
  if (!priority) {
    return <span style={{ color: "#94a3b8" }}>—</span>;
  }
  const tone = priorityToneMap[priority.toLowerCase()] || "default";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ring-1 ring-inset ${toneClasses[tone]}`}
    >
      {priority}
    </span>
  );
};

const fluidSlideVariants: Variants = {
  enter: {
    y: 0,
    opacity: 1,
    scale: 1,
    transition: { type: "spring", bounce: 0.18, duration: 0.55 },
  },
  exit: {
    y: 48,
    opacity: 0,
    scale: 0.96,
    transition: { duration: 0.25, ease: "easeIn" },
  },
};

const sectionVariants: Variants = {
  hidden: { opacity: 0, y: 16, scale: 0.98 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { delay: 0.08 + i * 0.06, duration: 0.4, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fieldVariants: Variants = {
  hidden: { opacity: 0, x: -6 },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: { delay: i * 0.025, duration: 0.25, ease: "easeOut" },
  }),
};

const badgeVariants: Variants = {
  hidden: { opacity: 0, scale: 0.85 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { delay: 0.2, type: "spring", stiffness: 300, damping: 22 },
  },
};

const EntityDetailsModal: React.FC<EntityDetailsModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  eyebrow,
  avatarUrl,
  avatarIcon = "ti ti-building",
  badge,
  sections,
  onEdit,
}) => {
  const tone = badge?.tone || "default";
  const badgeStyle = toneBadgeStyles[tone];
  const iconBg = toneIconBg[tone] || toneIconBg.default;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="5xl"
      backdrop="opaque"
      placement="center"
      scrollBehavior="inside"
      hideCloseButton
      motionProps={{ variants: fluidSlideVariants }}
      classNames={{
        wrapper: "z-[99999]",
        backdrop: "z-[99999] bg-slate-900/35 backdrop-blur-sm",
        base: "rounded-2xl shadow-2xl overflow-hidden",
        header: "!block !p-0 !flex-none !rounded-t-2xl",
      }}
    >
      <ModalContent>
        {(close) => (
          <>
            <ModalHeader>
              <div
                style={{
                  width: "100%",
                  background: "linear-gradient(135deg, #fafbff 0%, #f0f0ff 50%, #f5f3ff 100%)",
                  borderBottom: "1px solid #e8e5f0",
                  padding: "22px 28px",
                  borderRadius: "16px 16px 0 0",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                  }}
                >
                  <div
                    style={{
                      flexShrink: 0,
                      width: 56,
                      height: 56,
                      borderRadius: 16,
                      overflow: "hidden",
                      background: avatarUrl ? "#e0e7ff" : "linear-gradient(135deg, #6366f1, #8b5cf6)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px solid #e0e7ff",
                      boxShadow: "0 2px 8px rgba(99,102,241,0.12)",
                    }}
                  >
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt={title}
                        style={{
                          width: 56,
                          height: 56,
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    ) : (
                      <i
                        className={avatarIcon}
                        style={{ fontSize: 24, color: "#fff" }}
                      />
                    )}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    {eyebrow && (
                      <p
                        style={{
                          margin: "0 0 2px 0",
                          fontSize: 11,
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          color: "#8b5cf6",
                        }}
                      >
                        {eyebrow}
                      </p>
                    )}
                    <h3
                      style={{
                        margin: 0,
                        fontSize: 20,
                        fontWeight: 700,
                        color: "#1e1b4b",
                        letterSpacing: "-0.01em",
                        lineHeight: 1.3,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {title}
                    </h3>
                    {subtitle && (
                      <p
                        style={{
                          margin: "2px 0 0 0",
                          fontSize: 13,
                          fontWeight: 500,
                          color: "#6b7280",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {subtitle}
                      </p>
                    )}
                  </div>

                  {badge && (
                    <motion.span
                      initial="hidden"
                      animate="visible"
                      variants={badgeVariants}
                      style={{
                        display: "inline-flex",
                        flexShrink: 0,
                        alignItems: "center",
                        gap: 6,
                        borderRadius: 9999,
                        padding: "5px 14px",
                        fontSize: 12,
                        fontWeight: 600,
                        color: badgeStyle.color,
                        background: badgeStyle.bg,
                        border: `1px solid ${badgeStyle.border}`,
                      }}
                    >
                      <span
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          background: badgeStyle.color,
                          opacity: 0.8,
                        }}
                      />
                      {badge.label}
                    </motion.span>
                  )}

                  <button
                    type="button"
                    onClick={close}
                    style={{
                      marginLeft: 4,
                      flexShrink: 0,
                      width: 34,
                      height: 34,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 10,
                      border: "1px solid #e5e7eb",
                      background: "#fff",
                      color: "#9ca3af",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#f9fafb";
                      e.currentTarget.style.color = "#4b5563";
                      e.currentTarget.style.borderColor = "#d1d5db";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#fff";
                      e.currentTarget.style.color = "#9ca3af";
                      e.currentTarget.style.borderColor = "#e5e7eb";
                    }}
                  >
                    <i className="ti ti-x" style={{ fontSize: 16 }} />
                  </button>
                </div>
              </div>
            </ModalHeader>

            <ModalBody style={{ padding: "24px 28px", background: "#fff" }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 20,
                }}
              >
                {sections.map((section, sIndex) => (
                  <motion.div
                    key={section.title}
                    custom={sIndex}
                    initial="hidden"
                    animate="visible"
                    variants={sectionVariants}
                    style={{
                      gridColumn: "1 / -1",
                      borderRadius: 16,
                      border: "1px solid #eef2f7",
                      background: "#fcfcfe",
                      padding: 20,
                      transition: "border-color 0.25s, box-shadow 0.25s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#ddd6fe";
                      e.currentTarget.style.boxShadow = "0 4px 16px rgba(99,102,241,0.06)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "#eef2f7";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        marginBottom: 16,
                        paddingBottom: 12,
                        borderBottom: "1px solid #f1f5f9",
                      }}
                    >
                      {section.icon && (
                        <div
                          style={{
                            width: 34,
                            height: 34,
                            minWidth: 34,
                            minHeight: 34,
                            borderRadius: 10,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: iconBg,
                            boxShadow: "0 2px 8px rgba(99,102,241,0.15)",
                          }}
                        >
                          <i
                            className={section.icon}
                            style={{ fontSize: 16, color: "#fff" }}
                          />
                        </div>
                      )}
                      <h4
                        style={{
                          margin: 0,
                          fontSize: 14,
                          fontWeight: 700,
                          color: "#1e293b",
                          letterSpacing: "-0.01em",
                        }}
                      >
                        {section.title}
                      </h4>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, 1fr)",
                        gap: 10,
                      }}
                    >
                      {section.fields.map((field, fIndex) => (
                        <motion.div
                          key={field.label}
                          custom={fIndex}
                          initial="hidden"
                          animate="visible"
                          variants={fieldVariants}
                          style={{
                            gridColumn: field.fullWidth ? "1 / -1" : undefined,
                            borderRadius: 12,
                            background: "#f8fafc",
                            padding: "10px 14px",
                            border: "1px solid transparent",
                            transition: "all 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#f1f5f9";
                            e.currentTarget.style.borderColor = "#e2e8f0";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#f8fafc";
                            e.currentTarget.style.borderColor = "transparent";
                          }}
                        >
                          <dt
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              textTransform: "uppercase",
                              letterSpacing: "0.06em",
                              color: "#94a3b8",
                              margin: 0,
                            }}
                          >
                            {field.label}
                          </dt>
                          <dd
                            style={{
                              marginTop: 4,
                              fontSize: 14,
                              fontWeight: 600,
                              color: "#334155",
                              wordBreak: "break-word",
                            }}
                          >
                            {field.value === null ||
                            field.value === undefined ||
                            field.value === "" ? (
                              <span style={{ fontWeight: 400, color: "#cbd5e1" }}>—</span>
                            ) : (
                              field.value
                            )}
                          </dd>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            </ModalBody>

            <ModalFooter
              style={{
                borderTop: "1px solid #f1f5f9",
                background: "#fafbff",
                padding: "16px 28px",
              }}
            >
              <button
                type="button"
                onClick={close}
                style={{
                  borderRadius: 12,
                  border: "1px solid #e2e8f0",
                  background: "#fff",
                  padding: "9px 20px",
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#475569",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#f8fafc";
                  e.currentTarget.style.borderColor = "#cbd5e1";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#fff";
                  e.currentTarget.style.borderColor = "#e2e8f0";
                }}
              >
                Close
              </button>
              {onEdit && (
                <button
                  type="button"
                  onClick={() => {
                    close();
                    onEdit();
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    borderRadius: 12,
                    border: "none",
                    padding: "9px 20px",
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#fff",
                    cursor: "pointer",
                    background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                    boxShadow: "0 4px 12px rgba(99,102,241,0.3)",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = "0 6px 20px rgba(99,102,241,0.4)";
                    e.currentTarget.style.transform = "translateY(-1px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(99,102,241,0.3)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <i className="ti ti-edit" style={{ fontSize: 16 }} />
                  Edit
                </button>
              )}
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default EntityDetailsModal;
